import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import FloatingBookingButton from "./components/FloatingBookingButton";

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const SophrologueVille = lazy(() => import("./pages/SophrologueVille"));
const FAQ = lazy(() => import("./pages/FAQ"));
const LibererLaPeur = lazy(() => import("./pages/LibererLaPeur"));
const Tarifs = lazy(() => import("./pages/Tarifs"));
const CabinetAcces = lazy(() => import("./pages/CabinetAcces"));
const AvisGoogle = lazy(() => import("./pages/AvisGoogle"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Prefetch Blog pages after initial load for faster navigation
const prefetchBlog = () => {
  import("./pages/Blog");
  import("./pages/BlogPost");
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
    },
  },
});

// Optimized loading fallback with minimal DOM
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Prefetch component that triggers after initial render
const PrefetchTrigger = () => {
  useEffect(() => {
    // Prefetch after idle or after 2 seconds
    if ('requestIdleCallback' in window) {
      (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(prefetchBlog);
    } else {
      setTimeout(prefetchBlog, 2000);
    }
  }, []);
  return null;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <FloatingBookingButton />
          <PrefetchTrigger />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/sophrologue/:ville" element={<SophrologueVille />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/categorie/liberer-la-peur" element={<LibererLaPeur />} />
              <Route path="/tarifs-remboursement" element={<Tarifs />} />
              <Route path="/cabinet-acces" element={<CabinetAcces />} />
              <Route path="/avis-google" element={<AvisGoogle />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
